'use client';

import { useCallback, useEffect, useState } from 'react';
import { tutorialPhase, type Tutorial, type TutorialStep } from '../tutorial';
import { BoardMessageType, type Board } from '../types';
import type { CheckHintContext, GamePlayActions, GamePlayState } from './playState';

export interface TutorialSteps {
  currentStep: number;
  /** What the current step says and marks. Null outside the tutorial. */
  tutorialStep: TutorialStep | null;
  /** Whether the board takes input during the current step. */
  canInteract: boolean;
  nextStep: () => void;
  /** Back to the start of the current phase, on a blank board. */
  resetTutorial: () => void;
}

export interface TutorialStepsOptions<BoardT extends Board, CellT> {
  gameState: GamePlayState<BoardT, CellT> & GamePlayActions<BoardT, CellT> & CheckHintContext;
  tutorial: Tutorial<BoardT, CellT>;
  enabled: boolean;
  /** The step to open on, usually read from wherever the client keeps it (a URL, a route param). */
  initialStep: number;
  /**
   * Told of every step change, so the client can record it where a reload or a later phase's board
   * will find it. Moving into a phase on another board is the client's to act on: this hook only
   * walks the steps on the board it was given.
   */
  onStepChange?: (step: number) => void;
}

/**
 * Walks a game's tutorial on the board in play: which step we're on, what that step says, and the marks
 * it puts on the board. The platform half — where the step is remembered, how a new phase's board is
 * loaded, where the player goes on leaving — belongs to each client.
 */
export function useTutorialSteps<BoardT extends Board, CellT>({
  gameState,
  tutorial,
  enabled,
  initialStep,
  onStepChange,
}: TutorialStepsOptions<BoardT, CellT>): TutorialSteps {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const { board, playData, complete, handleReset, setBoardMessages } = gameState;

  const goToStep = useCallback(
    (step: number) => {
      setCurrentStep(step);
      onStepChange?.(step);
    },
    [onStepChange]
  );

  const nextStep = useCallback(() => {
    if (currentStep < tutorial.steps.length - 1) goToStep(currentStep + 1);
  }, [currentStep, goToStep, tutorial]);

  const resetTutorial = useCallback(() => {
    goToStep(tutorialPhase(tutorial, currentStep).firstStep);
    handleReset();
  }, [currentStep, goToStep, handleReset, tutorial]);

  // A returning player may have a finished play saved for a tutorial board, so entering a phase at its
  // first step always starts from a blank grid.
  useEffect(() => {
    if (!enabled || !board) return;
    if (currentStep === tutorialPhase(tutorial, currentStep).firstStep) handleReset();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleReset isn't stable; only a step or board change should reset
  }, [currentStep, enabled, board]);

  /*
    Evaluated in an effect rather than during render because a step can see its own last result (see
    TutorialContext.previous), which has to be remembered between moves. The updater form reads that
    result without depending on it, so a step returning something new each time can't loop.
  */
  const [evaluated, setEvaluated] = useState<{ step: number; result: TutorialStep } | null>(null);
  useEffect(() => {
    if (!enabled || !board) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- derived from the step and the board, see above
    setEvaluated((last) => ({
      step: currentStep,
      result: tutorial.steps[currentStep]({
        board,
        cells: playData.cells,
        complete,
        previous: last?.step === currentStep ? last.result : undefined,
      }),
    }));
  }, [enabled, tutorial, currentStep, board, playData.cells, complete]);
  const tutorialStep = evaluated?.result ?? null;

  // The board reads its marks from the board messages, as it does a hint's.
  useEffect(() => {
    if (!tutorialStep) return;
    setBoardMessages([{ type: BoardMessageType.tutorial, message: null, markedCells: tutorialStep.marks ?? [] }]);
  }, [tutorialStep, setBoardMessages]);

  return { currentStep, tutorialStep, canInteract: tutorialStep?.canInteract ?? true, nextStep, resetTutorial };
}
